import { List } from "immutable";
import { createSelector } from "reselect";
import i18n from "~/i18n.js";
import { sortCompare } from "~/utils/utils.jsx";
import { getFormDraft } from "./selector.forms.jsx";

export const getAllLabels = (state) => state.labels;

export const getAllTaskLabels = createSelector(getAllLabels, (labels) => labels.filter((l) => l.class === `TaskLabel`));

export const getAllGlobalTaskLabels = createSelector(getAllTaskLabels, (labels) =>
  labels
    .filter((l) => l.is_global)
    .toList()
    .toJS()
);

export const getAllGlobaTaskLabelsSortedByName = createSelector(getAllGlobalTaskLabels, (labels) =>
  labels.sort((firstLabel, secondLabel) => firstLabel.name.localeCompare(secondLabel.name))
);

export const getAllProjectLabels = createSelector(getAllLabels, (labels) =>
  labels.filter((l) => l.class === `ProjectLabel`)
);

export const getLabelsByIds = createSelector(
  getAllLabels,
  (_state, ids) => ids,
  (labels, ids) => {
    const projectsLabels = ids
      .map((id) => {
        if (labels.get(`${id}`)) {
          return { id, name: labels.get(`${id}`).name };
        }
      })
      .filter(Boolean)
      .sort((a, b) => sortCompare(a.name.toLowerCase(), b.name.toLowerCase()));

    return ids.includes(0) ? [{ id: 0, name: i18n.t("No label") }, ...projectsLabels] : projectsLabels;
  }
);

export const getAllProjectLabelsSortedByName = createSelector(getAllProjectLabels, (labels) =>
  labels
    .sort((firstLabel, secondLabel) => firstLabel.get(`name`).localeCompare(secondLabel.get(`name`)))
    .toList()
    .toJS()
);

const getProjectTaskLabelsById = (state, projectId) => state.projectTaskLabels.get(`${projectId}`);

export const getTaskLabelById = (state, id) => {
  return getAllLabels(state).get(`${id}`);
};

export const getLabelIdsFromTaskDraft = createSelector(
  (state, projectId, formKey) => getFormDraft(state, formKey),
  (draft) => draft.get(`labelIds`, List()).toArray()
);

export const makeTaskLabelsByProjectSelector = () => {
  return createSelector(
    getProjectTaskLabelsById,
    getAllTaskLabels,
    getLabelIdsFromTaskDraft,
    (state, project_id) => project_id,
    (projectTaskLabels = [], labels, selectedLabelIdsFromDraft = [], project_id) => {
      const ids = [...projectTaskLabels, ...selectedLabelIdsFromDraft];
      return labels.filter(
        (label) => label.project_id === +project_id || ids.indexOf(label.id) > -1 || label.is_global
      );
    }
  );
};

export const makeNotGlobalTaskLabelsByProjectSelector = () => {
  return createSelector(
    getProjectTaskLabelsById,
    getAllTaskLabels,
    getLabelIdsFromTaskDraft,
    (state, project_id) => project_id,
    (projectTaskLabels = [], labels, selectedLabelIdsFromDraft = [], project_id) => {
      const ids = [...projectTaskLabels, ...selectedLabelIdsFromDraft];
      const filteredLabels = labels.filter(
        (label) => !label.is_global && (label.project_id === +project_id || ids.indexOf(label.id) > -1)
      );
      return filteredLabels
        .sort((firstLabel, secondLabel) => firstLabel.name.localeCompare(secondLabel.name))
        .toList()
        .toJS();
    }
  );
};

export const makeGetBasicLabelInfoByIdSelector = () =>
  createSelector(getTaskLabelById, (label) => {
    return {
      name: label && label.name,
      color: label && label.lighter_text_color,
    };
  });
